import { useEffect, useMemo } from "react";
import useWebSocket from "react-use-websocket";
import useSWR from "swr";
import { useShallow } from "zustand/shallow";
import { useGetTrade } from "./market/hooks/useGetTrade";
import { Pair, PairsState, usePairs } from "./store/pairs";
import { Data24hBuyApi, usePrice } from "./store/price";

type OHLC = {
  time: number;
  close: number;
  low: number;
  open: number;
  symbol: string;
  volume: number;
};

type HistoryLite = {
  time: number;
  value: number;
};

async function fetchHistoryHoldStation({ symbol, time = "1m" }: { symbol: string; time?: string }) {
  if (!symbol) {
    return [];
  }

  try {
    const res = await fetch(
      `https://pricefeed.holdstation.com/history/${symbol}?timeframe=${time}&to=${Date.now() / 1000}`
    );
    const data: OHLC[] = await res.json();

    const formatted: HistoryLite[] = data.reverse().map((item) => ({
      time: item.time,
      value: item.close,
    }));

    return formatted;
  } catch {
    throw new Error("fetchHistoryHoldStation");
  }
}

function useGetPairNoSupport24hChange() {
  const pairsData = usePairs(useShallow((state: PairsState) => state.data));

  const listPairNoSupport24h = useMemo(() => pairsData.filter((item) => item.isNoSupport24h), [pairsData]);

  return listPairNoSupport24h;
}

async function getPriceLast1Day({ abort, symbol }: { abort: boolean; symbol: Pair[] }) {
  if (abort || !symbol.length) {
    return undefined;
  }

  const results = await Promise.all(
    symbol.map((sym) =>
      fetchHistoryHoldStation({ symbol: sym.symbol, time: "1d" })
        .then((res) => {
          const firstItem = [...(res ?? [])].reverse()?.[0];
          return firstItem ? { ...firstItem, pairIndex: sym.pairIndex } : null;
        })
        .catch(() => null)
    )
  );

  const resultMap: Record<number, Data24hBuyApi> = {};
  for (const item of results) {
    if (item) {
      resultMap[item.pairIndex] = item;
    }
  }

  return resultMap;
}
export function useGetHistory1D() {
  const listPairNoSupport24h = useGetPairNoSupport24hChange();

  const shouldFetch = listPairNoSupport24h.length > 0;

  const { data, error, isLoading } = useSWR(
    shouldFetch ? ["history1D", ...listPairNoSupport24h] : null,
    () =>
      getPriceLast1Day({
        abort: false,
        symbol: listPairNoSupport24h,
      }),
    {
      dedupingInterval: 10000,
    }
  );

  return {
    data,
    error,
    listPairNoSupport24h,
    isLoading,
  };
}

export function SubscribePrice() {
  const { lastMessage } = useWebSocket("wss://pricefeed.holdstation.com/v2", {
    shouldReconnect: () => true,
    share: true,
  });
  const [setData, setPrice24hChangePair] = usePrice(
    useShallow((state) => [state.setData, state.setPrice24hChangePair])
  );

  const { data: dataPrice1D, listPairNoSupport24h } = useGetHistory1D();

  const listIndexNoSupport24h = useMemo(
    () => listPairNoSupport24h.map((item) => item.pairIndex),
    [listPairNoSupport24h]
  );

  useEffect(() => {
    if (!lastMessage) {
      return;
    }

    const data: [string, string, string] = lastMessage.data.split("|");

    if (listIndexNoSupport24h.includes(Number(data[0])) && dataPrice1D) {
      setPrice24hChangePair(dataPrice1D, data);
    }

    setData(data);
  }, [dataPrice1D, lastMessage, listIndexNoSupport24h, setData, setPrice24hChangePair]);

  return null;
}

export default function SubscribeTrade() {
  useGetTrade();
  return null;
}
